import React, { useState } from "react";
import moment from "moment";
import Header from "../header/header";
import { Button, Table, Modal, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import app from "../../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, writeBatch, getFirestore } from "firebase/firestore";
import Papa from "papaparse";
import styles from "./stats.module.scss";

export const ImportPage = () => {
  const [csvData, setCsvData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [importResult, setImportResult] = useState(null);
  const PREVIEW_PAGE_SIZE = 100; // Only show 100 rows at a time

  // Define the exact column order for the preview table
  const tableColumns = [
    "playerProfileId",
    "date",
    "fullName",
    "season",
    "event",
    "type",
    "team",
    "opponent",
    "result",
    "min",
    "fgFga",
    "fgPct",
    "threePThreePA",
    "threeFgPct",
    "ftFta",
    "ftPct",
    "orb",
    "drb",
    "trb",
    "ast",
    "blk",
    "stl",
    "to",
    "pf",
    "pts",
    "source",
    "dateAdded"
  ];

  const mapRowToDoc = (row) => ({
    // Include source field in Firestore document
    playerProfileId: row.playerProfileId || "",
    date: moment(row.date, "MM/DD/YYYY").format("MM/DD/YYYY") || "",
    fullName: row.fullName || "",
    season: row.season || "",
    event: row.event || "",
    type: row.type || "",
    team: row.team || "",
    opponent: row.opponent || "",
    result: row.result || "",
    min: row.min || "",
    fgFga: row.fgFga || "",
    fgPct: row.fgPct || "",
    threePThreePA: row.threePThreePA || "",
    threeFgPct: row.threeFgPct || "",
    ftFta: row.ftFta || "",
    ftPct: row.ftPct || "",
    orb: row.orb || "",
    drb: row.drb || "",
    trb: row.trb || "",
    ast: row.ast || "",
    blk: row.blk || "",
    stl: row.stl || "",
    to: row.to || "",
    pf: row.pf || "",
    pts: row.pts || "",
    source: row.source || "",
    dateAdded: row.dateAdded || moment().format("MM/DD/YYYY")
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return toast.error("No file selected.");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length) {
          console.error(result.errors);
          return toast.error("Error parsing CSV file.");
        }
        // Add dateAdded field to each row with current date
        const currentDate = moment().format("MM/DD/YYYY");
        const dataWithDateAdded = result.data.map(row => ({
          ...row,
          dateAdded: currentDate
        }));
        setCsvData(dataWithDateAdded);
        setPreviewPage(1); // Reset to first page when new file is loaded
        toast.success(`Successfully parsed ${dataWithDateAdded.length} rows. Showing preview of first ${PREVIEW_PAGE_SIZE} rows.`);
      },
      error: (err) => toast.error(`Parsing error: ${err.message}`)
    });
  };

  // Get paginated preview data
  const getPreviewData = () => {
    const startIndex = (previewPage - 1) * PREVIEW_PAGE_SIZE;
    const endIndex = startIndex + PREVIEW_PAGE_SIZE;
    return csvData.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(csvData.length / PREVIEW_PAGE_SIZE);

  const handleImport = async () => {
    if (!csvData.length) return toast.error("No data to import.");

    setIsUploading(true);
    setShowConfirmModal(false);
    
    const BATCH_SIZE = 500; // Firestore batch limit
    const firestore = getFirestore(app);
    const statsCollection = collection(firestore, "game-log-stats");
    let importedCount = 0;
    let failedCount = 0;
    const totalRows = csvData.length;
    const errors = [];
    const startTime = Date.now();

    try {
      // Process in batches
      for (let i = 0; i < csvData.length; i += BATCH_SIZE) {
        try {
          const batch = writeBatch(firestore);
          const batchData = csvData.slice(i, i + BATCH_SIZE);
          let batchErrors = [];

          batchData.forEach((row, rowIndex) => {
            try {
              const docData = mapRowToDoc(row);
              const docRef = doc(statsCollection);
              batch.set(docRef, docData);
            } catch (rowError) {
              batchErrors.push({
                row: i + rowIndex + 1,
                error: rowError.message || "Unknown error processing row"
              });
            }
          });

          await batch.commit();
          importedCount += batchData.length;
          failedCount += batchErrors.length;
          errors.push(...batchErrors);

          // Show progress for large imports
          if (totalRows > 1000) {
            const progress = Math.round((importedCount / totalRows) * 100);
            toast.info(`Importing... ${importedCount.toLocaleString()} / ${totalRows.toLocaleString()} (${progress}%)`, {
              autoClose: 2000,
            });
          }
        } catch (batchError) {
          // If a batch fails, try to import rows individually
          const batchData = csvData.slice(i, i + BATCH_SIZE);
          for (let j = 0; j < batchData.length; j++) {
            try {
              const docData = mapRowToDoc(batchData[j]);
              await addDoc(statsCollection, docData);
              importedCount++;
            } catch (rowError) {
              failedCount++;
              errors.push({
                row: i + j + 1,
                error: rowError.message || "Unknown error"
              });
            }
          }
        }
      }

      const endTime = Date.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);

      // Set import result
      setImportResult({
        success: true,
        imported: importedCount,
        failed: failedCount,
        total: totalRows,
        duration: duration,
        errors: errors.slice(0, 10) // Show first 10 errors
      });

      setCsvData([]);
      setShowResultModal(true);
    } catch (err) {
      console.error(err);
      setImportResult({
        success: false,
        imported: importedCount,
        failed: failedCount,
        total: totalRows,
        error: err.message || "Unknown error occurred during import",
        errors: errors.slice(0, 10)
      });
      setShowResultModal(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      const statsCollection = collection(db, "game-log-stats");
      const querySnapshot = await getDocs(statsCollection);
      
      const deletePromises = querySnapshot.docs.map((docSnapshot) =>
        deleteDoc(doc(db, "game-log-stats", docSnapshot.id))
      );
      
      await Promise.all(deletePromises);
      toast.success(`Successfully deleted ${querySnapshot.docs.length} documents from Game Log Stats collection.`);
    } catch (err) {
      console.error(err);
      toast.error(`Delete failed: ${err.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <Header />
      <div className={`${styles.container} ${styles.importPage}`}>
        <Row>
          <Col>
            <h4>Import Game Log Stats</h4>
            <div style={{ marginBottom: "20px" }}>
              <Button
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
                disabled={isDeleting || isUploading}
                style={{ marginBottom: "20px" }}
              >
                {isDeleting ? "Deleting..." : "Delete All Game Log Stats"}
              </Button>
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className={styles.fileInput}
              style={{ padding: "20px 0 40px" }}
            />

            {csvData.length > 0 && (
              <>
                <div style={{ marginBottom: "15px" }}>
                  <p>
                    <strong>Total rows:</strong> {csvData.length.toLocaleString()}
                    {csvData.length > PREVIEW_PAGE_SIZE && (
                      <span> | <strong>Showing:</strong> {((previewPage - 1) * PREVIEW_PAGE_SIZE + 1).toLocaleString()} - {Math.min(previewPage * PREVIEW_PAGE_SIZE, csvData.length).toLocaleString()} (Page {previewPage} of {totalPages})</span>
                    )}
                  </p>
                </div>

                {csvData.length > PREVIEW_PAGE_SIZE && (
                  <div style={{ marginBottom: "15px", display: "flex", gap: "10px", alignItems: "center" }}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setPreviewPage(1)}
                      disabled={previewPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setPreviewPage(prev => Math.max(1, prev - 1))}
                      disabled={previewPage === 1}
                    >
                      Previous
                    </Button>
                    <span>Page {previewPage} of {totalPages}</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setPreviewPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={previewPage === totalPages}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setPreviewPage(totalPages)}
                      disabled={previewPage === totalPages}
                    >
                      Last
                    </Button>
                  </div>
                )}

                <div style={{ maxHeight: "600px", overflowY: "auto", overflowX: "auto", marginBottom: "20px" }}>
                  <Table striped bordered hover responsive className={styles.statsTable} style={{ width: "auto", tableLayout: "auto" }}>
                    <thead className={styles.tableHeader} style={{ position: "sticky", top: 0, backgroundColor: "#000", zIndex: 10 }}>
                      <tr>
                        {tableColumns.map(col => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getPreviewData().map((row, rowIndex) => {
                        const actualIndex = (previewPage - 1) * PREVIEW_PAGE_SIZE + rowIndex;
                        return (
                          <tr key={actualIndex} className={styles.tableRow}>
                            {tableColumns.map(col => (
                              <td key={col} className={styles.tableCell}>
                                {row[col] ?? ""}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <Button
                  className={styles.importButton}
                  onClick={() => setShowConfirmModal(true)}
                  disabled={isUploading}
                >
                  {isUploading ? "Importing..." : "Confirm Import"}
                </Button>
              </>
            )}
          </Col>
        </Row>
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Import</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to import these {csvData.length} rows?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleImport}>
            {isUploading ? "Importing..." : "Yes, Import"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete All Game Log Stats</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Warning:</strong> This action will permanently delete ALL documents in the Game Log Stats collection.</p>
          <p>This action cannot be undone. Are you sure you want to continue?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteAll}>
            {isDeleting ? "Deleting..." : "Yes, Delete All"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Result Modal */}
      <Modal 
        show={showResultModal} 
        onHide={() => {
          setShowResultModal(false);
          setImportResult(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {importResult?.success ? "✅ Import Successful" : "❌ Import Failed"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {importResult?.success ? (
            <div>
              <div style={{ marginBottom: "20px" }}>
                <p style={{ fontSize: "18px", marginBottom: "10px" }}>
                  <strong>Successfully imported {importResult.imported.toLocaleString()} out of {importResult.total.toLocaleString()} rows!</strong>
                </p>
                <p style={{ color: "#666", marginBottom: "5px" }}>
                  Duration: {importResult.duration} seconds
                </p>
                {importResult.failed > 0 && (
                  <p style={{ color: "#ff9800", marginBottom: "5px" }}>
                    ⚠️ {importResult.failed} row(s) failed to import
                  </p>
                )}
              </div>
              
              {importResult.failed > 0 && importResult.errors.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  <p><strong>Error Details (showing first {importResult.errors.length}):</strong></p>
                  <div style={{ maxHeight: "200px", overflowY: "auto", backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "4px" }}>
                    {importResult.errors.map((err, idx) => (
                      <div key={idx} style={{ marginBottom: "8px", fontSize: "12px" }}>
                        <strong>Row {err.row}:</strong> {err.error}
                      </div>
                    ))}
                    {importResult.errors.length < importResult.failed && (
                      <p style={{ fontSize: "12px", color: "#666", marginTop: "10px" }}>
                        ... and {importResult.failed - importResult.errors.length} more error(s)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: "20px" }}>
                <p style={{ fontSize: "18px", marginBottom: "10px", color: "#dc3545" }}>
                  <strong>Import Failed!</strong>
                </p>
                <p style={{ marginBottom: "10px" }}>
                  <strong>Error:</strong> {importResult?.error || "Unknown error occurred"}
                </p>
                {importResult && (
                  <div>
                    <p style={{ marginBottom: "5px" }}>
                      Successfully imported: {importResult.imported.toLocaleString()} rows
                    </p>
                    {importResult.failed > 0 && (
                      <p style={{ marginBottom: "5px", color: "#ff9800" }}>
                        Failed: {importResult.failed.toLocaleString()} rows
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {importResult?.errors && importResult.errors.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  <p><strong>Error Details:</strong></p>
                  <div style={{ maxHeight: "200px", overflowY: "auto", backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "4px" }}>
                    {importResult.errors.map((err, idx) => (
                      <div key={idx} style={{ marginBottom: "8px", fontSize: "12px" }}>
                        <strong>Row {err.row}:</strong> {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant={importResult?.success ? "primary" : "secondary"} 
            onClick={() => {
              setShowResultModal(false);
              setImportResult(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
